<template>
    <div id="barcode-status-print">
        <header class="header-operator">
            <span class="header__back" @click="goBack">
                <BackIcon />
            </span>
            <span class="header__title">Configurar e imprimir</span>
            <button type="button" class="header__action" :disabled="!printItems.length" @click="printBarcodes">
                Imprimir
            </button>
        </header>

        <div class="body-content">
            <aside class="config-panel">
                <section class="config-block">
                    <button
                        type="button"
                        class="config-block__toggle"
                        :aria-expanded="isSectionOpen('model')"
                        @click="toggleSection('model')"
                    >
                        <h3>Modelo</h3>
                        <span class="config-block__arrow" :class="{ open: isSectionOpen('model') }" />
                    </button>
                    <div v-show="isSectionOpen('model')" class="config-block__body">
                        <label class="field">
                            <span>Tipo de etiqueta</span>
                            <select v-model="model">
                                <option value="status-sheet">Folha de status</option>
                                <option value="status-single">Status único</option>
                                <option value="appointment">Apontamento de produção</option>
                                <option value="custom">Layout personalizado</option>
                            </select>
                        </label>

                        <label v-if="model === 'status-single'" class="field">
                            <span>Status</span>
                            <select v-model="singleStatus">
                                <option v-for="status in statusOptions" :key="status.code" :value="status.code">
                                    {{ status.label }}
                                </option>
                            </select>
                        </label>
                    </div>
                </section>

                <section class="config-block">
                    <button
                        type="button"
                        class="config-block__toggle"
                        :aria-expanded="isSectionOpen('content')"
                        @click="toggleSection('content')"
                    >
                        <h3>Conteúdo e quantidade</h3>
                        <span class="config-block__arrow" :class="{ open: isSectionOpen('content') }" />
                    </button>
                    <div v-show="isSectionOpen('content')" class="config-block__body">
                        <label v-if="showAppointmentQty" class="field">
                            <span>Quantidade apontada (peças)</span>
                            <input v-model.number="appointmentQty" type="number" min="1" step="1" />
                        </label>
                        <p v-if="showAppointmentQty && !appointmentPayload" class="field-hint">
                            Abra esta tela a partir de uma ordem para gerar {{ appointmentExample }}.
                        </p>
                        <p v-else-if="appointmentPayload" class="field-hint">
                            Código: {{ appointmentPayload }}
                        </p>

                        <label v-if="model !== 'custom'" class="field">
                            <span>Cópias de cada etiqueta</span>
                            <input v-model.number="copies" type="number" min="1" max="50" step="1" />
                        </label>

                        <div v-if="model === 'custom'" class="custom-builder">
                            <div class="custom-builder__actions">
                                <select v-model="addStatusCode">
                                    <option v-for="status in statusOptions" :key="status.code" :value="status.code">
                                        {{ status.label }}
                                    </option>
                                </select>
                                <button type="button" class="ghost-button" @click="addStatusItem">Adicionar status</button>
                                <button type="button" class="ghost-button" @click="addAppointmentItem">Adicionar apontamento</button>
                            </div>
                            <ul class="layout-list">
                                <li v-for="(item, index) in customItems" :key="item.id">
                                    <strong>{{ itemLabel(item) }}</strong>
                                    <input v-model.number="item.copies" type="number" min="1" max="50" title="Cópias" />
                                    <button type="button" :disabled="index === 0" @click="moveItem(index, -1)">↑</button>
                                    <button type="button" :disabled="index === customItems.length - 1" @click="moveItem(index, 1)">↓</button>
                                    <button type="button" class="remove" @click="removeItem(index)">×</button>
                                </li>
                            </ul>
                            <p v-if="!customItems.length" class="field-hint">Adicione status ou apontamento e organize a ordem de impressão.</p>
                        </div>
                    </div>
                </section>

                <section class="config-block">
                    <button
                        type="button"
                        class="config-block__toggle"
                        :aria-expanded="isSectionOpen('layout')"
                        @click="toggleSection('layout')"
                    >
                        <h3>Layout</h3>
                        <span class="config-block__arrow" :class="{ open: isSectionOpen('layout') }" />
                    </button>
                    <div v-show="isSectionOpen('layout')" class="config-block__body">
                        <label class="field">
                            <span>Colunas</span>
                            <select v-model.number="columns">
                                <option :value="1">1 coluna</option>
                                <option :value="2">2 colunas</option>
                                <option :value="3">3 colunas</option>
                                <option :value="4">4 colunas</option>
                            </select>
                        </label>
                        <label class="field">
                            <span>Tamanho da etiqueta</span>
                            <select v-model="labelSize">
                                <option value="compact">Compacto</option>
                                <option value="standard">Padrão</option>
                                <option value="large">Grande</option>
                            </select>
                        </label>
                        <label class="field">
                            <span>Espaçamento</span>
                            <select v-model.number="gapMm">
                                <option :value="2">2 mm</option>
                                <option :value="4">4 mm</option>
                                <option :value="6">6 mm</option>
                                <option :value="8">8 mm</option>
                            </select>
                        </label>
                        <label class="check">
                            <input v-model="showTitle" type="checkbox" />
                            Mostrar nome do status
                        </label>
                        <label class="check">
                            <input v-model="showPayload" type="checkbox" />
                            Mostrar código abaixo das barras
                        </label>
                        <label class="check">
                            <input v-model="showBorder" type="checkbox" />
                            Borda para recorte
                        </label>
                    </div>
                </section>

                <section class="config-block">
                    <button
                        type="button"
                        class="config-block__toggle"
                        :aria-expanded="isSectionOpen('page')"
                        @click="toggleSection('page')"
                    >
                        <h3>Página de impressão</h3>
                        <span class="config-block__arrow" :class="{ open: isSectionOpen('page') }" />
                    </button>
                    <div v-show="isSectionOpen('page')" class="config-block__body">
                        <label class="field">
                            <span>Tamanho do papel</span>
                            <select v-model="paper">
                                <option value="a4">A4 (210 × 297 mm)</option>
                                <option value="a5">A5 (148 × 210 mm)</option>
                                <option value="letter">Carta (216 × 279 mm)</option>
                                <option value="thermal80">Térmica 80 mm</option>
                                <option value="label50">Etiqueta 50 × 25 mm</option>
                            </select>
                        </label>
                        <label class="field">
                            <span>Orientação</span>
                            <select v-model="orientation" :disabled="paper === 'label50'">
                                <option value="portrait">Retrato</option>
                                <option value="landscape">Paisagem</option>
                            </select>
                        </label>
                        <label class="field">
                            <span>Margem (mm)</span>
                            <input v-model.number="marginMm" type="number" min="0" max="30" step="1" />
                        </label>
                        <p class="field-hint">{{ printItems.length }} etiqueta(s) em {{ printPages.length }} página(s). Imprima em 100%, sem ajustar à página.</p>
                    </div>
                </section>
            </aside>

            <section class="preview-panel">
                <div class="preview-toolbar">
                    <strong>Pré-visualização</strong>
                    <span>{{ paperLabel }} · {{ printColumns }} col · {{ printItems.length }} un. · {{ printPages.length }} pág.</span>
                </div>
                <div class="preview-stage">
                    <p v-if="!printItems.length" class="empty-preview">Nada para imprimir. Ajuste o modelo e as quantidades.</p>
                    <div
                        v-for="(page, pageIndex) in printPages"
                        :key="`page-${pageIndex}`"
                        class="sheet-scaler"
                        :style="scalerStyle"
                    >
                        <div class="print-sheet" :style="sheetStyle">
                            <article
                                v-for="(item, index) in page"
                                :key="`${item.payload}-${pageIndex}-${index}`"
                                class="print-label"
                                :class="{ bordered: showBorder }"
                                :style="labelStyle"
                            >
                                <span v-if="showTitle" class="print-label__title">{{ item.label }}</span>
                                <img :src="item.src" :alt="item.payload" :style="barcodeImageStyle" />
                                <code v-if="showPayload">{{ item.payload }}</code>
                            </article>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import { buildCode128DataUri, statusBarcodePayload, appointmentBarcodePayload } from "./code128Barcode";

const STORAGE_KEY = "mysfc-barcode-print-settings";
const STATUS_OPTIONS = [
    { code: "2", label: "Setup" },
    { code: "3", label: "Iniciado" },
    { code: "4", label: "Produção" },
    { code: "5", label: "Finalizado" },
    { code: "99", label: "Parada" }
];
const PAPERS = {
    a4: { label: "A4", width: 210, height: 297 },
    a5: { label: "A5", width: 148, height: 210 },
    letter: { label: "Carta", width: 216, height: 279 },
    thermal80: { label: "Térmica 80 mm", width: 80, height: 200 },
    label50: { label: "Etiqueta 50 × 25 mm", width: 50, height: 25 }
};
const LABEL_HEIGHT = { compact: 16, standard: 22, large: 28 };

export default {
    components: {
        BackIcon: () => import("@/assets/images/icons/back.svg")
    },
    data() {
        return {
            statusOptions: STATUS_OPTIONS,
            model: "status-sheet",
            singleStatus: "4",
            appointmentQty: 50,
            copies: 1,
            customItems: [],
            addStatusCode: "2",
            columns: 2,
            labelSize: "standard",
            gapMm: 4,
            showTitle: true,
            showPayload: true,
            showBorder: true,
            paper: "a4",
            orientation: "portrait",
            marginMm: 10,
            customId: 1,
            openSections: {}
        };
    },
    computed: {
        orderNumber() {
            return this.$route.query.order ? String(this.$route.query.order).trim() : "";
        },
        operationNumber() {
            return this.$route.query.operation_number ? String(this.$route.query.operation_number).trim() : "";
        },
        appointmentPayload() {
            const quantity = Number(this.appointmentQty);
            if (!Number.isInteger(quantity) || quantity <= 0) return "";
            if (!this.orderNumber || !this.operationNumber) return "";
            return appointmentBarcodePayload(this.orderNumber, this.operationNumber, quantity);
        },
        appointmentExample() {
            return "A001;10;50";
        },
        showAppointmentQty() {
            if (this.model === "appointment") return true;
            if (this.model === "custom") {
                return this.customItems.some((item) => item.type === "appointment");
            }
            return false;
        },
        selectedItems() {
            if (this.model === "status-sheet") {
                return STATUS_OPTIONS.map((status) => ({ type: "status", code: status.code, copies: this.copies }));
            }
            if (this.model === "status-single") {
                return [{ type: "status", code: this.singleStatus, copies: this.copies }];
            }
            if (this.model === "appointment") {
                return [{ type: "appointment", copies: this.copies }];
            }
            return this.customItems;
        },
        printItems() {
            const items = [];
            this.selectedItems.forEach((item) => {
                const built = this.buildPrintItem(item);
                if (!built) return;
                const copies = Math.min(50, Math.max(1, Number(item.copies) || 1));
                for (let i = 0; i < copies; i += 1) items.push(built);
            });
            return items.slice(0, 120);
        },
        printColumns() {
            return this.paper === "label50" || this.paper === "thermal80" ? 1 : this.columns;
        },
        labelBlockHeightMm() {
            const barcode = LABEL_HEIGHT[this.labelSize] || 22;
            const pad = this.showBorder ? 6 : 2;
            const title = this.showTitle ? 5 : 0;
            const payload = this.showPayload ? 5 : 0;
            const children = 1 + (this.showTitle ? 1 : 0) + (this.showPayload ? 1 : 0);
            const flexGaps = Math.max(0, children - 1) * 2;
            const border = this.showBorder ? 1 : 0;
            return barcode + pad + title + payload + flexGaps + border + 2;
        },
        itemsPerPage() {
            const usableHeight = this.paperSize.height - this.marginMm * 2;
            const rowStride = this.labelBlockHeightMm + this.gapMm;
            const rows = Math.floor((usableHeight + this.gapMm) / rowStride);
            return Math.max(1, rows) * this.printColumns;
        },
        printPages() {
            if (!this.printItems.length) return [];
            const perPage = this.itemsPerPage;
            const pages = [];
            for (let i = 0; i < this.printItems.length; i += perPage) {
                pages.push(this.printItems.slice(i, i + perPage));
            }
            return pages;
        },
        paperSize() {
            const paper = PAPERS[this.paper] || PAPERS.a4;
            if (this.paper === "label50") return paper;
            if (this.orientation === "landscape") {
                return { ...paper, width: paper.height, height: paper.width };
            }
            return paper;
        },
        paperLabel() {
            return `${this.paperSize.width} × ${this.paperSize.height} mm`;
        },
        previewScale() {
            const maxWidth = 560;
            return Math.min(0.72, maxWidth / (this.paperSize.width * 3.78));
        },
        scalerStyle() {
            return {
                width: `${this.paperSize.width * this.previewScale}mm`,
                height: `${this.paperSize.height * this.previewScale}mm`,
                "--preview-scale": this.previewScale
            };
        },
        sheetStyle() {
            return {
                width: `${this.paperSize.width}mm`,
                height: `${this.paperSize.height}mm`,
                minHeight: `${this.paperSize.height}mm`,
                padding: `${this.marginMm}mm`,
                gridTemplateColumns: `repeat(${this.printColumns}, minmax(0, 1fr))`,
                gap: `${this.gapMm}mm`
            };
        },
        labelStyle() {
            return {
                padding: this.showBorder ? "3mm" : "1mm"
            };
        },
        barcodeImageStyle() {
            return {
                height: `${LABEL_HEIGHT[this.labelSize] || 22}mm`
            };
        },
        pageSizeRule() {
            const named = { a4: "A4", a5: "A5", letter: "letter" }[this.paper];
            if (named) return `${named} ${this.orientation}`;
            return `${this.paperSize.width}mm ${this.paperSize.height}mm`;
        }
    },
    watch: {
        model() {
            if (this.model === "custom" && !this.customItems.length) {
                this.customItems = STATUS_OPTIONS.map((status) => this.createItem("status", status.code));
            }
            if (this.paper === "thermal80" || this.paper === "label50") this.columns = 1;
            this.persistSettings();
        },
        paper(value) {
            if (value === "thermal80" || value === "label50") {
                this.columns = 1;
                this.orientation = "portrait";
            }
            this.persistSettings();
        },
        copies() { this.persistSettings(); },
        appointmentQty() { this.persistSettings(); },
        columns() { this.persistSettings(); },
        labelSize() { this.persistSettings(); },
        gapMm() { this.persistSettings(); },
        showTitle() { this.persistSettings(); },
        showPayload() { this.persistSettings(); },
        showBorder() { this.persistSettings(); },
        orientation() { this.persistSettings(); },
        marginMm() { this.persistSettings(); },
        customItems: {
            handler() { this.persistSettings(); },
            deep: true
        }
    },
    created() {
        this.restoreSettings();
    },
    beforeDestroy() {
        this.removePrintPageStyle();
    },
    methods: {
        goBack() {
            const prefix = localStorage.getItem("prefix") || this.$route.params.prefix;
            this.$router.push({
                name: "operator-barcode-status",
                params: { prefix },
                query: { ...this.$route.query }
            });
        },
        isSectionOpen(section) {
            return Boolean(this.openSections[section]);
        },
        toggleSection(section) {
            this.$set(this.openSections, section, !this.openSections[section]);
        },
        createItem(type, code) {
            this.customId += 1;
            return {
                id: this.customId,
                type,
                code: code || "2",
                copies: 1
            };
        },
        addStatusItem() {
            this.customItems.push(this.createItem("status", this.addStatusCode));
        },
        addAppointmentItem() {
            this.customItems.push(this.createItem("appointment"));
        },
        removeItem(index) {
            this.customItems.splice(index, 1);
        },
        moveItem(index, direction) {
            const target = index + direction;
            if (target < 0 || target >= this.customItems.length) return;
            const items = this.customItems.slice();
            const [moved] = items.splice(index, 1);
            items.splice(target, 0, moved);
            this.customItems = items;
        },
        itemLabel(item) {
            if (item.type === "appointment") return "Apontamento";
            const status = STATUS_OPTIONS.find((entry) => entry.code === item.code);
            return status ? status.label : "Status";
        },
        buildPrintItem(item) {
            if (item.type === "appointment") {
                if (!this.appointmentPayload) return null;
                return {
                    label: "Apontamento",
                    payload: this.appointmentPayload,
                    src: buildCode128DataUri(this.appointmentPayload)
                };
            }
            const status = STATUS_OPTIONS.find((entry) => entry.code === item.code);
            if (!status) return null;
            const payload = statusBarcodePayload(status.code);
            return {
                label: status.label,
                payload,
                src: buildCode128DataUri(payload)
            };
        },
        persistSettings() {
            const payload = {
                model: this.model,
                singleStatus: this.singleStatus,
                appointmentQty: this.appointmentQty,
                copies: this.copies,
                customItems: this.customItems,
                columns: this.columns,
                labelSize: this.labelSize,
                gapMm: this.gapMm,
                showTitle: this.showTitle,
                showPayload: this.showPayload,
                showBorder: this.showBorder,
                paper: this.paper,
                orientation: this.orientation,
                marginMm: this.marginMm
            };
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
            } catch (error) {}
        },
        restoreSettings() {
            try {
                const raw = localStorage.getItem(STORAGE_KEY);
                if (!raw) return;
                const saved = JSON.parse(raw);
                Object.keys(saved).forEach((key) => {
                    if (this[key] !== undefined && saved[key] !== undefined) this[key] = saved[key];
                });
            } catch (error) {}
        },
        removePrintPageStyle() {
            this.removePrintClone();
        },
        removePrintClone() {
            const clone = document.getElementById("barcode-print-clone");
            if (clone) clone.remove();
            const iframe = document.getElementById("barcode-print-frame");
            if (iframe) iframe.remove();
        },
        escapeHtml(value) {
            return String(value)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;");
        },
        buildPrintDocument() {
            const { width, height } = this.paperSize;
            const barcodeHeight = LABEL_HEIGHT[this.labelSize] || 22;
            const pad = this.showBorder ? "3mm" : "1mm";
            const pages = this.printPages.map((page) => {
                const labels = page.map((item) => {
                    const title = this.showTitle
                        ? `<span class="title">${this.escapeHtml(item.label)}</span>`
                        : "";
                    const payload = this.showPayload
                        ? `<code>${this.escapeHtml(item.payload)}</code>`
                        : "";
                    const bordered = this.showBorder ? " bordered" : "";
                    return `<article class="print-label${bordered}" style="padding:${pad}">${title}<img src="${item.src}" alt="${this.escapeHtml(item.payload)}" style="height:${barcodeHeight}mm">${payload}</article>`;
                }).join("");
                return `<section class="print-sheet">${labels}</section>`;
            }).join("");
            return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Etiquetas</title>
<style>
  @page { size: ${this.pageSizeRule}; margin: 0; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    width: 100%;
  }
  * { box-sizing: border-box; }
  body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
  .print-sheet {
    display: grid;
    align-content: start;
    grid-template-columns: repeat(${this.printColumns}, minmax(0, 1fr));
    gap: ${this.gapMm}mm;
    padding: ${this.marginMm}mm;
    width: 100%;
    height: ${height}mm;
    min-height: ${height}mm;
    background: #fff;
    overflow: hidden;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
  }
  .print-sheet:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  .print-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 2mm;
    width: 100%;
    min-width: 0;
    background: #fff;
  }
  .print-label.bordered { border: 1px dashed #c5cbd3; }
  .print-label img {
    display: block;
    width: 100%;
    object-fit: contain;
    background: #fff;
  }
  .title, code {
    display: block;
    width: 100%;
    text-align: center;
    font-size: 11px;
    font-family: Inter, "Segoe UI", Arial, sans-serif;
    color: #4c4541;
  }
  .title { font-weight: 700; }
  @media print {
    html, body {
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .print-sheet {
      width: 100% !important;
      height: ${height}mm !important;
    }
  }
</style>
</head>
<body>${pages}</body>
</html>`;
        },
        printBarcodes() {
            if (!this.printItems.length) return;
            this.removePrintClone();
            const { width, height } = this.paperSize;
            const iframe = document.createElement("iframe");
            iframe.id = "barcode-print-frame";
            iframe.setAttribute("aria-hidden", "true");
            iframe.style.cssText = `position:fixed;left:0;top:0;width:${width}mm;height:${height}mm;border:0;opacity:0;pointer-events:none;z-index:-1;`;
            document.body.appendChild(iframe);
            const doc = iframe.contentDocument;
            doc.open();
            doc.write(this.buildPrintDocument());
            doc.close();
            const onAfterPrint = () => {
                iframe.contentWindow.removeEventListener("afterprint", onAfterPrint);
                this.removePrintClone();
            };
            iframe.contentWindow.addEventListener("afterprint", onAfterPrint);
            const doPrint = () => iframe.contentWindow.print();
            if (doc.readyState === "complete") {
                setTimeout(doPrint, 50);
            } else {
                iframe.onload = () => setTimeout(doPrint, 50);
            }
        }
    }
};
</script>

<style lang="scss">
.app-content-operator:has(#barcode-status-print) {
    padding: 0 !important;
}

@media print {
    .print-sheet {
        transform: none !important;
    }
}
</style>

<style lang="scss" scoped>
#barcode-status-print {
    --orange: #ea7100;
    --ink: #18212f;
    --muted: #738091;
    --line: #dfe3e8;
    --surface: #ffffff;

    color: var(--ink);
    width: 100%;
    min-height: calc(100vh - 120px);
    font-family: Inter, "Segoe UI", Arial, sans-serif;
    display: flex;
    flex-direction: column;
    background: #f5f6f7;

    * {
        box-sizing: border-box;
    }

    .header-operator {
        width: 100%;
        border-top: 1px solid #cfc4be;
        border-bottom: 1px solid #cfc4be;
        padding: 15.5px 29px;
        z-index: 1;
        gap: 16px;
        display: flex;
        align-items: center;
        background: #ffffff;
        color: #4c4541;
        box-shadow: 0px 8px 30px rgba(58, 46, 38, 0.1);

        .header__back {
            display: flex;
            cursor: pointer;
        }

        .header__title {
            font-family: "Raleway";
            font-style: normal;
            font-weight: 600;
            font-size: 22px;
            line-height: 30px;
        }

        .header__action {
            margin-left: auto;
            border: 1px solid #ea7100;
            background: #ea7100;
            color: #fff;
            border-radius: 8px;
            padding: 8px 14px;
            font-weight: 700;
            font-size: 14px;
            cursor: pointer;

            &:disabled {
                opacity: 0.55;
                cursor: not-allowed;
            }
        }
    }

    .body-content {
        width: 100%;
        padding: 16px 29px 24px;
        display: grid;
        grid-template-columns: minmax(280px, 340px) 1fr;
        gap: 18px;
        align-items: start;

        @media (max-width: 1100px) {
            grid-template-columns: 1fr;
            padding: 16px 16px 24px;
        }

        @media (max-width: 560px) {
            padding: 12px 10px 16px;
        }
    }
}

.config-panel,
.preview-panel {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 14px;
}

.config-panel {
    padding: 8px 0;
}

.config-block {
    border-bottom: 1px solid var(--line);

    &:last-child {
        border-bottom: 0;
    }
}

.config-block__toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    border: 0;
    background: transparent;
    padding: 14px 18px;
    cursor: pointer;
    color: inherit;
    text-align: left;

    h3 {
        margin: 0;
        font-size: 13px;
        font-weight: 700;
    }
}

.config-block__arrow {
    width: 8px;
    height: 8px;
    border-right: 2px solid #738091;
    border-bottom: 2px solid #738091;
    transform: rotate(45deg);
    transition: transform 0.15s ease;
    flex-shrink: 0;

    &.open {
        transform: rotate(-135deg);
    }
}

.config-block__body {
    padding: 0 18px 14px;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 10px;

    span {
        font-size: 12px;
        font-weight: 600;
        color: #4c4541;
    }

    input,
    select {
        width: 100%;
        border: 1px solid #cfd5dc;
        border-radius: 8px;
        padding: 8px 10px;
        color: var(--ink);
        background: #fff;
        outline: none;

        &:focus {
            border-color: var(--orange);
            box-shadow: 0 0 0 3px rgba(234, 113, 0, .13);
        }
    }
}

.field-hint {
    margin: 0 0 10px;
    font-size: 12px;
    color: var(--muted);
}

.check {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    margin-bottom: 8px;
}

.custom-builder__actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    margin-bottom: 10px;
}

.ghost-button {
    border: 1px solid #cfd5dc;
    background: #fff;
    border-radius: 8px;
    padding: 8px 10px;
    font-weight: 700;
    font-size: 12px;
    color: #4c4541;
    cursor: pointer;
}

.layout-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;

    li {
        display: grid;
        grid-template-columns: 1fr 58px 32px 32px 32px;
        gap: 6px;
        align-items: center;
        padding: 6px;
        border: 1px solid var(--line);
        border-radius: 8px;
        background: #fafafa;
    }

    strong {
        font-size: 12px;
    }

    input,
    button {
        height: 32px;
        border: 1px solid #cfd5dc;
        border-radius: 6px;
        background: #fff;
    }

    button {
        cursor: pointer;
        font-weight: 700;
    }

    .remove {
        color: #e5252a;
    }
}

.preview-toolbar {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--line);
    font-size: 12px;

    span {
        color: var(--muted);
    }
}

.preview-stage {
    overflow: auto;
    padding: 20px;
    background: #e8eaed;
    min-height: 520px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
}

.sheet-scaler {
    margin: 0 auto;
    overflow: hidden;
}

.print-sheet {
    background: #fff;
    box-shadow: 0 10px 30px rgba(0, 0, 0, .12);
    display: grid;
    align-content: start;
    overflow: hidden;
    transform: scale(var(--preview-scale, 1));
    transform-origin: top left;
}

.print-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2mm;
    break-inside: avoid;
    page-break-inside: avoid;
    background: #fff;

    &.bordered {
        border: 1px dashed #c5cbd3;
    }

    img {
        width: 100%;
        object-fit: contain;
        background: #fff;
    }

    code {
        font-size: 11px;
        color: #4c4541;
        text-align: center;
        word-break: break-all;
    }
}

.print-label__title {
    font-size: 11px;
    font-weight: 700;
}

.empty-preview {
    grid-column: 1 / -1;
    text-align: center;
    color: var(--muted);
    font-size: 13px;
}

@media (max-width: 560px) {
    .header-operator {
        padding: 12px 16px !important;
        flex-wrap: wrap;
    }

    .header__action {
        width: 100%;
        margin-left: 0 !important;
    }

    .layout-list li {
        grid-template-columns: 1fr 1fr;
    }
}

@media print {
    .header-operator,
    .config-panel,
    .preview-toolbar {
        display: none !important;
    }

    .body-content {
        display: block !important;
        padding: 0 !important;
        background: #fff !important;
    }

    .preview-panel {
        border: none !important;
        border-radius: 0 !important;
        background: #fff !important;
    }

    .preview-stage {
        padding: 0 !important;
        background: #fff !important;
        overflow: visible !important;
        min-height: 0 !important;
    }

    .sheet-scaler {
        width: auto !important;
        height: auto !important;
        overflow: visible !important;
    }

    .print-sheet {
        box-shadow: none !important;
        transform: none !important;
    }
}
</style>
